import React from 'react';
import { AuditEvent } from '../api/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Calendar, 
  User, 
  Globe, 
  Code, 
  Hash, 
  FileText,
  Database
} from 'lucide-react';

interface AuditEventDetailsProps {
  event: AuditEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

const AuditEventDetails: React.FC<AuditEventDetailsProps> = ({ event, isOpen, onClose }) => {
  if (!event) return null;

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  };

  const getActionBadgeColor = (action: string) => {
    if (action.endsWith('_FAILED')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    }
    if (action.includes('DELETE') || action.includes('REMOVE') || action.includes('REVOKE') || 
        action.includes('OFFBOARD') || action.includes('REJECT')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    }
    if (action.includes('UPDATE') || action.includes('RESET_PASSWORD') || action.includes('TRIGGER') ||
        action.includes('REQUEST') || action.includes('SUBMIT_POLICY_REVIEW') || action.includes('ASSESSMENT')) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
  };

  const getStatusBadgeColor = (statusCode: number) => {
    if ((statusCode >= 200 && statusCode < 300) || statusCode === 101) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    } else if (statusCode >= 500) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  };

  const renderRequestData = (data: Record<string, any>) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="text-sm text-muted-foreground italic">
          No request data captured for this event
        </div>
      );
    }

    return (
      <pre className="text-xs bg-muted p-3 rounded border overflow-x-auto overflow-y-auto whitespace-pre-wrap max-h-[400px]">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">
            Audit Event Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Event Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Action</label>
                <div className="mt-1">
                  <Badge className={getActionBadgeColor(event.action)}>
                    {event.action}
                  </Badge>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Resource</label>
                <div className="mt-1 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{event.resource}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Method</label>
                <div className="mt-1 flex items-center gap-2">
                  <Code className="h-4 w-4 text-gray-400" />
                  <Badge variant="outline">{event.method}</Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status Code</label>
                <div className="mt-1">
                  <Badge className={getStatusBadgeColor(event.status_code)}>
                    {event.status_code}
                  </Badge>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">User</label>
                <div className="mt-1 flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium">{event.user_email}</div>
                    <div className="text-sm text-gray-500">{event.user_role}</div>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</label>
                <div className="mt-1 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-400" />
                  <span className="font-mono text-sm">{event.ip_address || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Timestamp and ID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Timestamp</label>
              <div className="mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="font-mono text-sm">{formatTimestamp(event.timestamp)}</span>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Event ID</label>
              <div className="mt-1 flex items-center gap-2">
                <Hash className="h-4 w-4 text-gray-400" />
                <span className="font-mono text-xs break-all">{event.id}</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Request Data */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Database className="h-5 w-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Request Data
              </label>
            </div>
            {renderRequestData(event.request_data || {})}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuditEventDetails;